import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import type { AssetLibraryEmbedConfig } from '../assetLibraryEmbedConfig';
import {
  toAssetLibraryMediaSelection,
  type AssetLibraryMediaSelection,
} from '../assetLibraryMediaSelection';
import type { AssetLibraryDisplayItem } from '../utils';

type AssetLibraryMediaSelectionContextValue = {
  isStagedSelection: boolean;
  isSelected: (assetID: string) => boolean;
  toggleAsset: (asset: AssetLibraryDisplayItem) => void;
  selectedCount: number;
  getSelections: () => AssetLibraryMediaSelection[];
  confirmSelection: () => void;
  cancelSelection: () => void;
};

const AssetLibraryMediaSelectionContext =
  createContext<AssetLibraryMediaSelectionContextValue | null>(null);

export function AssetLibraryMediaSelectionProvider({
  embedConfig,
  children,
}: {
  embedConfig: AssetLibraryEmbedConfig | null;
  children: ReactNode;
}) {
  const mediaSelection = embedConfig?.mediaSelection ?? null;
  const [selectedById, setSelectedById] = useState<
    Map<string, AssetLibraryMediaSelection>
  >(() => new Map());

  useEffect(() => {
    if (!mediaSelection) {
      setSelectedById(new Map());
      return;
    }

    const initial = mediaSelection.initialSelection ?? [];
    setSelectedById(
      new Map(initial.map((item) => [item.assetID, item])),
    );
  }, [mediaSelection, mediaSelection?.initialSelection]);

  const isStagedSelection = Boolean(mediaSelection);

  const isSelected = useCallback(
    (assetID: string) => selectedById.has(assetID),
    [selectedById],
  );

  const toggleAsset = useCallback(
    (asset: AssetLibraryDisplayItem) => {
      if (!mediaSelection) {
        return;
      }

      const selection = toAssetLibraryMediaSelection(asset);
      if (!selection) {
        return;
      }

      setSelectedById((previous) => {
        const next = new Map(previous);

        if (next.has(asset.assetID)) {
          next.delete(asset.assetID);
          return next;
        }

        if (mediaSelection.mode === 'single') {
          next.clear();
          next.set(asset.assetID, selection);
          return next;
        }

        const maxSelection = mediaSelection.maxSelection;
        if (maxSelection != null && next.size >= maxSelection) {
          return previous;
        }

        next.set(asset.assetID, selection);
        return next;
      });
    },
    [mediaSelection],
  );

  const getSelections = useCallback(
    () => Array.from(selectedById.values()),
    [selectedById],
  );

  const confirmSelection = useCallback(() => {
    if (!mediaSelection) {
      return;
    }

    mediaSelection.onConfirm(getSelections());
    setSelectedById(new Map());
  }, [getSelections, mediaSelection]);

  const cancelSelection = useCallback(() => {
    mediaSelection?.onCancel?.();
    setSelectedById(new Map());
  }, [mediaSelection]);

  const value = useMemo(
    () => ({
      isStagedSelection,
      isSelected,
      toggleAsset,
      selectedCount: selectedById.size,
      getSelections,
      confirmSelection,
      cancelSelection,
    }),
    [
      cancelSelection,
      confirmSelection,
      getSelections,
      isSelected,
      isStagedSelection,
      selectedById.size,
      toggleAsset,
    ],
  );

  return (
    <AssetLibraryMediaSelectionContext.Provider value={value}>
      {children}
    </AssetLibraryMediaSelectionContext.Provider>
  );
}

export function useAssetLibraryMediaSelection() {
  return useContext(AssetLibraryMediaSelectionContext);
}
